import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "primary" | "danger" | "warn" | "ghost";
  size?: "sm" | "md";
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "md", ...props }, ref) => {
    const base =
      "inline-flex items-center gap-1.5 rounded-md font-medium transition-colors duration-100 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)] tracking-[-0.165px]";

    const sizes = {
      sm: "px-2 py-1 text-xs",
      md: "px-3 py-1.5 text-sm",
    };

    const variants = {
      default:
        "bg-[rgba(255,255,255,0.04)] border border-[var(--color-border)] text-[var(--color-fg)] hover:bg-[rgba(255,255,255,0.08)]",
      primary:
        "bg-[var(--color-accent-bg)] text-white hover:bg-[var(--color-accent-hover)] border border-transparent",
      danger:
        "bg-[rgba(239,68,68,0.1)] text-[#fca5a5] border border-[rgba(239,68,68,0.2)] hover:bg-[rgba(239,68,68,0.2)]",
      warn:
        "bg-[rgba(245,158,11,0.1)] text-[#fcd34d] border border-[rgba(245,158,11,0.2)] hover:bg-[rgba(245,158,11,0.2)]",
      ghost:
        "bg-transparent text-[var(--color-fg-muted)] hover:bg-[rgba(255,255,255,0.04)] border border-transparent",
    };

    return (
      <button
        ref={ref}
        className={cn(base, sizes[size], variants[variant], className)}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";
